// Plain-language release notes, newest first. Shown in Settings ("What's New")
// and automatically the first time a phone opens a version it hasn't seen.
export const CHANGELOG = [
  {
    version: '1.11',
    sections: [
      {
        title: 'New functionalities',
        items: [
          'Switching tabs has a short animation, which can be changed or turned off in Settings → This phone.',
        ],
      },
      {
        title: 'Miscellaneous',
        items: [
          'The app opens on the Temperature tab.',
          'The app was rebuilt behind the scenes: it downloads less and opens faster after each update.',
          'The dosing and safety rules are now checked automatically before every update reaches your phone.',
        ],
      },
    ],
  },
  {
    version: '1.10',
    // Sectioned rather than one flat list: this release is long, and
    // "what's new for me" is easier to find under a heading. Older entries
    // keep plain `items`; ChangelogModal renders either shape.
    sections: [
      {
        title: 'Highlights',
        items: [
          'A cleaner layout that fits the Calculator on one screen: a slim banner at the top, and the tabs at the bottom within thumb reach.',
          'Pick the child once, in the top banner — it applies to every tab.',
          'One banner says when the next dose can be given, e.g. "TOO SOON — Paracetamol was given 2h ago. Wait until 16:00 (in 2h) before another dose."',
          'The dose is shown as the "Maximum dose", rounded down to the syringe\'s 0.1 ml mark. Adjust it with − and +: it turns yellow ("High dose") a little above, and red (can\'t be registered) beyond 10% over or the 1 g / 400 mg per-dose limit.',
          'The app opens with no signal, showing the last synced data, and starts faster.',
        ],
      },
      {
        title: 'New functionalities',
        items: [
          'Deleting a record or a temperature reading can be undone — no more "Are you sure?".',
          'The Calculator flags a child\'s weight that hasn\'t been updated in a while ("still right?"), so it can be confirmed or updated right there.',
          'With a temperature from the last 4 hours, the Calculator shows KKH\'s fever advice for the medicine — tap "KKH" to open the source page.',
          'Records and the Temperature tab\'s readings are grouped by day (Today, Yesterday…).',
          '"Last 24 Hours" shows how many mg are left for each medicine.',
          'Appearance has a new "Automatic" setting that follows the phone\'s light/dark mode.',
        ],
      },
      {
        title: 'Bug fixes',
        items: [
          'Changing the Concentration after calculating kept the dose worked out for the previous bottle — it now clears, so press Calculate again.',
          'A cleared date/time could leave "Recording…" stuck; empty and future times are now refused with a message.',
          'Deleting an older record no longer removes the "Last registered dose" Undo.',
          'Editing a dose\'s time in Records no longer changes the weight it was given at.',
        ],
      },
      {
        title: 'Miscellaneous',
        items: [
          'Calmer look: thinner borders, a softer red for warnings, and the dosing rules folded into one quiet line.',
          'Concentration and Calculate share a line, as do the temperature and Record; the dosing guidance is one line, e.g. "Max 3 doses per day, 7.1 ml each, every 6–8h".',
          'The green "can be given now" banner and the fever hint step aside once a dose is calculated, and right after registering only "✓ recorded" shows.',
          'The 24 h limit warning appears in the dose box, and the duplicate 24 h box is gone.',
          'The Calculator no longer shows "Weight: …" while the weight is up to date.',
          'Records follows the child picked at the top ("All children" shows everyone), and its timeline folds away.',
          'Settings lists the children first, with "+ Add child"; phone name, appearance and Sign out are under "This phone".',
          'Messages appear at the bottom of the screen instead of pop-ups.',
          'The app, and each tab, opens at the top of the screen.',
          'Records has a book icon.',
        ],
      },
    ],
  },
  {
    version: '1.9',
    items: [
      'Any concentration can now be edited or removed in Settings, including the built-in ones, with "Restore removed" to bring the built-ins back.',
      'Under "Other", medicines are now buttons grouped by category, and several can be logged at once — e.g. antihistamine + rash cream.',
      'Records entries now show the date and time beside the child\'s name, and how long ago it was ("3 hours ago", "2 days ago") underneath.',
      'Temperature readings now show which phone logged them, like doses do, and the phone\'s name is in brackets.',
      'Temperature readings no longer carry a "Fever" label — the colour already shows it.',
      'The Text export is now semicolon-separated with a header row, so the same export reads as text and opens as a spreadsheet.',
      'Exports now read: name, date and time, type, then the medicine or temperature.',
      'The timeline graph has a new 5d range.',
      'Records now shows the last 7 days, with a "Date Range" filter to go further back.',
      'Tapping several medicines at once on the graph now shows the date once, then each medicine with its time.',
    ],
  },
  {
    version: '1.8.2',
    items: [
      'Medicines given at the same time now show a small count on the graph, so you can see at a glance that a marker holds more than one.',
    ],
  },
  {
    version: '1.8.1',
    items: [
      'Tapping medicines given at the same time on the graph now lists all of them, instead of only showing one.',
    ],
  },
  {
    version: '1.8',
    items: [
      'Fixed: the Calculator could say a dose "can be given now" when the other medicine had just been given.',
      'The next-dose banner now says how long ago the medicine was given and when the next dose is due, and keeps counting down.',
      'The 24-hour tracker is now visible up front and covers both painkillers — one bar each, hidden when there\'s nothing to show.',
      'Paracetamol (blue) and ibuprofen (purple) now use the same colours in the Calculator and on the timeline.',
      'After calculating, the tracker previews what the dose would add.',
      'The timeline graph has new 12h and 48h ranges, and opens on the smallest one that fits that child\'s records from the past week — 36h of data opens at 48h.',
      'Added "‹ Older" / "Newer ›" buttons under the graph to step back through history beyond the last 7 days, with "Now" to jump back.',
      'Tapping a medicine on the graph now shows the strength too, e.g. "Paracetamol 5.0 ml (120 mg/5 ml)".',
      'Fixed: logging two temperatures (or two "Other" medicines) in a row could save the second one with the first one\'s time. The date field now keeps showing the real current time. Setting a time by hand still works as before.',
      'Fixed: after pressing Calculate, the dose panel kept its original numbers even if a dose arrived from the other phone. It now updates live, including the "Cannot Register" safety block.',
      'Temperature readings in Records can now be edited (child, time, °C), like doses already could — not just deleted.',
      'Timeline times are now labelled at round hours (midnight, 09:00, noon…) instead of relative to now.',
      'The timeline graph now shows at the top of Records too, with a child picker.',
      'Records opens on the child with the most recent entry — the one currently being treated — and switching child re-picks the range to fit that child.',
    ],
  },
  {
    version: '1.7',
    items: [
      'The temperature filter is now "Hide Temperature" (unchecked by default, since temperature readings show up by default) instead of "Display Temperature" — leaving it unchecked no longer counts toward the active filter badge.',
    ],
  },
  {
    version: '1.6',
    items: [
      'Renamed the "History" tab to "Records".',
      'The child and medicine filters are now tucked behind a "Filters" button, so Records is less cluttered — a badge shows how many filters are active.',
    ],
  },
  {
    version: '1.5',
    items: [
      'Fixed a bug where logging an "Other" medication could record the wrong medicine if the list had been changed — it now always records exactly what\'s shown.',
      'Editing a dose now keeps the milligram total in sync with the millilitres.',
      'The "What\'s New" popup no longer appears on a brand-new device — only when there\'s an actual update.',
    ],
  },
  {
    version: '1.4',
    items: [
      'Other medicines now have a category (e.g. "Antibiotics") set in Settings — Paracetamol and Ibuprofen are always under "Painkillers".',
      'History\'s "Filter by Medicine" is now two-tiered: check a whole category, or pick individual medicines within it.',
    ],
  },
  {
    version: '1.3',
    items: [
      'History now has a "Filter by Medicine" option — tap to select any combination of medicines, alongside the child filter.',
      'Temperature readings now show up in History too, alongside doses, instead of only under the Temperature tab.',
      'A "Display Temperature" toggle controls whether temperature readings show up, in both History and Export.',
      'Export mirrors the on-screen child and medicine filters plus the Display Temperature toggle, so the exported summary always matches what you\'re looking at.',
      'History cards now have a clearer line between the child\'s name and the medication/date details below it.',
    ],
  },
  {
    version: '1.2',
    items: [
      'The Calculator now shows when the next dose is allowed up front, instead of only after pressing Calculate.',
      'Deleting a history entry now asks for confirmation first.',
      'Added a device nickname in Settings (e.g. "Mom\'s phone") shown on doses that phone logs, so History shows who logged what.',
      'Added an Export option in History — pick a child and how many days back, then share a plain-text summary (handy for a doctor visit).',
    ],
  },
  {
    version: '1.1',
    items: [
      "Added this changelog — check Settings any time to see what's changed, or read it once automatically when a new version opens.",
    ],
  },
  {
    version: '1.0',
    items: [
      'Renamed "Medication Type" to "Medication Name" in Settings and the Calculator, to make it clearer.',
      'In the "Other" medication form, the free-text field is now called "Details" and is optional.',
      'You can now set the date and time when logging an "Other" medication — it defaults to now, but can be changed for a dose given earlier.',
      'History entries are shown more compactly, so more fit on the screen at once.',
      'You can now edit a history entry (child, time, dose, or details) if you made a mistake logging it, instead of deleting and re-entering it.',
      'Added a "Default Child" picker at the top of the app — choose a child once and it\'s pre-selected everywhere else (Calculator, Temperature, History).',
      'Added a version number at the bottom of the app.',
      'In Settings, you can now rename an existing medication name in the "Other Meds" list.',
      'Fixed the tab buttons at the top and in Settings so their labels no longer get cut off on phone screens.',
    ],
  },
];
